// Internal
import { features } from '@/lib/features';
import { noop } from '@/lib/utils/miscUtils';
import { useShippingAddressChange } from './useShippingAddressChange';
import { useShippingChange } from './v1';
import type { OnShippingChangeWrapper } from '@/lib/utils/shipping/types';
import type { Noop } from '@/types';

// Returns the appropriate shipping address callback based on the integration.
// For info about the difference between `v1` and `v2`. See
// https://github.paypal.com/Checkout-R/checkoutuinodeweb/blob/develop/apps/weasley/src/lib/features/resolvers.ts#L546-L563
// OR
// https://engineering.paypalcorp.com/confluence/pages/viewpage.action?pageId=853380692
export const useShippingAddressCallbacks = ():
  | OnShippingChangeWrapper
  | Noop => {
  // The logic for invoking both v2 client-side and server-side shipping address
  // change callbacks is nearly identical. To avoid duplication, this hook handles both
  // cases and selects the appropriate callback to run based on feature flags.
  const onShippingAddressChange = useShippingAddressChange();

  // v1 client-side callback integration
  const onShippingChange = useShippingChange();

  if (
    features.isActive('isServerSideShippingCallbacksEligible') ||
    // V2 client-side callback integration
    features.isActive('isShippingCallbacksV2Eligible')
  ) {
    return onShippingAddressChange;
  }

  // Order here is unimportant; v1 and v2 callbacks
  // can not be eligible at the same time.
  if (features.isActive('isShippingCallbackV1Eligible')) {
    return onShippingChange;
  }

  return noop;
};
